/**
 * <deal-carousel>
 *
 * Horizontal product track where the card nearest the centre grows, with prev/next
 * buttons that step between cards. Shared by the Featured Deals section and the
 * product recommendations section.
 *
 * Deliberately a custom element rather than an inline <script>: the product
 * recommendations section is fetched lazily and injected with `innerHTML`, and inline
 * scripts do not execute when set that way. Custom elements do upgrade on insertion,
 * so `connectedCallback` runs in both the server-rendered and injected cases.
 *
 * Everything is resolved by ref within `this`, so several instances can coexist on a
 * page without colliding.
 */

const BASE_WIDTH = 220;
const CENTER_WIDTH = 360;

class DealCarousel extends HTMLElement {
  #track = null;
  #cards = [];
  #controller = new AbortController();

  /** Cards advanced per prev/next click. `data-step="4"` pages four at a time. */
  get #step() {
    const step = parseInt(this.dataset.step ?? '', 10);
    return Number.isFinite(step) && step > 0 ? step : 1;
  }

  connectedCallback() {
    this.#track = this.querySelector('[data-deal-carousel-track]');
    if (!this.#track) return;

    this.#cards = Array.from(this.#track.querySelectorAll('.fd-card'));
    if (!this.#cards.length) return;

    const { signal } = this.#controller;

    this.#track.addEventListener('scroll', this.#updateWidths, { passive: true, signal });
    window.addEventListener('resize', this.#updateWidths, { passive: true, signal });

    this.querySelector('[data-deal-carousel-prev]')?.addEventListener(
      'click',
      () => this.#scrollToIndex(this.#closestIndex() - this.#step),
      { signal }
    );

    this.querySelector('[data-deal-carousel-next]')?.addEventListener(
      'click',
      () => this.#scrollToIndex(this.#closestIndex() + this.#step),
      { signal }
    );

    // Wait for layout before the first measurement, otherwise offsetLeft is 0.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#scrollToIndex(this.#isFill ? 0 : Math.floor(this.#cards.length / 2));
        // Size the cards directly too. When the cards already fit the track there is
        // nothing to scroll, so no scroll event fires and the widths would never be
        // applied, leaving every card at the CSS fallback width.
        this.#updateWidths();
      });
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  /** True when CSS sizes the cards into equal columns (per_view / fill mode). */
  get #isFill() {
    return this.dataset.perView != null;
  }

  /**
   * Scale each card by how close it is to the centre of the viewport.
   * No-op in fill mode, where CSS owns the widths and setting --fd-w would override
   * the equal columns.
   */
  #updateWidths = () => {
    if (!this.#track || this.#isFill) return;

    const trackRect = this.#track.getBoundingClientRect();
    const viewCenter = this.#track.scrollLeft + trackRect.width / 2;
    const range = trackRect.width * 0.45;

    for (const card of this.#cards) {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const progress = Math.max(0, 1 - Math.abs(cardCenter - viewCenter) / range);
      const width = Math.round(BASE_WIDTH + (CENTER_WIDTH - BASE_WIDTH) * progress);

      card.style.setProperty('--fd-w', `${width}px`);
      card.style.zIndex = String(Math.round(progress * 10));
    }
  };

  /**
   * The card the carousel is currently "on": nearest the centre normally, or the
   * leftmost visible one in fill mode, where paging aligns cards to the left edge.
   */
  #closestIndex() {
    if (!this.#track) return 0;

    const trackRect = this.#track.getBoundingClientRect();
    const anchor = this.#isFill
      ? this.#track.scrollLeft
      : this.#track.scrollLeft + trackRect.width / 2;

    let closest = 0;
    let closestDistance = Infinity;

    this.#cards.forEach((card, index) => {
      const cardAnchor = this.#isFill ? card.offsetLeft : card.offsetLeft + card.offsetWidth / 2;
      const distance = Math.abs(cardAnchor - anchor);
      if (distance < closestDistance) {
        closestDistance = distance;
        closest = index;
      }
    });

    return closest;
  }

  /**
   * Bring the card at `index` into view, clamped to the available cards. Fill mode
   * aligns it to the left edge so a page of cards lines up with the track; otherwise
   * the card is centred, which is what the centre-grow effect keys off.
   */
  #scrollToIndex(index) {
    if (!this.#track) return;

    const clamped = Math.max(0, Math.min(this.#cards.length - 1, index));
    const card = this.#cards[clamped];
    if (!card) return;

    const trackRect = this.#track.getBoundingClientRect();
    this.#track.scrollTo({
      left: this.#isFill
        ? card.offsetLeft
        : card.offsetLeft + card.offsetWidth / 2 - trackRect.width / 2,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('deal-carousel')) {
  customElements.define('deal-carousel', DealCarousel);
}
